import React, { useState, useEffect, useCallback, Fragment } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../api';
import { Spinner, Toast, css, PageHeader, Label, Paginacion } from '../components/UI';
import { Icono } from '../components/Iconos';

// Reporte de compras por requisición: qué se solicitó, qué se aprobó (quién,
// cuándo y con qué comentario) y qué se rechazó (en qué etapa, con qué motivo del
// catálogo y su detalle). Las cifras las calcula el backend
// (routes/reporteRequisiciones.js), igual que para el MCP.

const POR_PAGINA = 25;
const RESULTADOS = [
  { id: '', label: 'Todas' },
  { id: 'aprobadas', label: 'Aprobadas' },
  { id: 'rechazada', label: 'Rechazadas' },
  { id: 'con_rechazo', label: 'Con algún rechazo' },
  { id: 'en_proceso', label: 'En proceso' },
  { id: 'completada', label: 'Con solicitud de pago' },
];
const ETAPA_RECHAZO = { area: 'Aprobación del área', aprobacion: 'Jefe de compras', autorizacion: 'Autorización de la compra', finanzas: 'Finanzas' };
const ESTADO_PAGO = { pendiente: 'Pendiente', aprobada: 'Aprobada', por_pagar: 'Por pagar', pagada: 'Pagada', rechazada: 'Rechazada', cancelada: 'Cancelada' };
const VACIOS = { desde: '', hasta: '', empresa: '', direccion: '', centroCostos: '', solicitante: '', comprador: '', resultado: '', etapaActual: '', etapaRechazo: '', categoria: '', proveedor: '', texto: '' };

const fmt = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtEntero = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-MX')}`;
const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fechaHora = (d) => (d ? new Date(d).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const dias = (n) => (n == null ? '—' : `${n} ${n === 1 ? 'día' : 'días'}`);
const inicioAnio = () => `${new Date().getFullYear()}-01-01`;

// Aprobaciones de una fila en texto plano (para la tabla y el Excel)
function aprobacionesTexto(a) {
  const out = [];
  for (const p of a.area || []) {
    if (p.estado === 'aprobado') out.push(`Área: ${p.por}${p.comentario ? ` ("${p.comentario}")` : ''}`);
  }
  if (a.jefeCompras) out.push(`Jefe de compras: ${a.jefeCompras.por}${a.jefeCompras.comentario ? ` ("${a.jefeCompras.comentario}")` : ''}`);
  if (a.autorizacion) out.push(`Autorizó: ${a.autorizacion.por}${a.autorizacion.comentario ? ` ("${a.autorizacion.comentario}")` : ''}`);
  if (a.finanzas?.estado === 'aprobada') out.push(`Finanzas: ${a.finanzas.por}${a.finanzas.comentario ? ` ("${a.finanzas.comentario}")` : ''}`);
  return out;
}

export default function ReporteRequisiciones() {
  const [filtros, setFiltros] = useState({ ...VACIOS, desde: inicioAnio() });
  const [texto, setTexto] = useState({ proveedor: '', texto: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [abierta, setAbierta] = useState(null);
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4500); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filtros).filter(([, v]) => v))).toString();
      setData(await api.get(`/reportes/requisiciones?${qs}`));
      setPage(1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filtros]);
  useEffect(() => { load(); }, [load]);
  // Los campos de texto filtran al dejar de escribir
  useEffect(() => {
    const t = setTimeout(() => setFiltros(f => (f.proveedor === texto.proveedor && f.texto === texto.texto ? f : { ...f, ...texto })), 450);
    return () => clearTimeout(t);
  }, [texto]);

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v }));
  const alternar = (k, v) => setFiltros(f => ({ ...f, [k]: f[k] === v ? '' : v }));
  const limpiar = () => { setFiltros({ ...VACIOS }); setTexto({ proveedor: '', texto: '' }); };
  const hayFiltros = Object.entries(filtros).some(([, v]) => v);

  const exportar = () => {
    if (!data?.filas?.length) return;
    const filas = data.filas.map(r => ({
      Folio: r.folio, Fecha: fecha(r.fecha), Solicitante: r.solicitante || '', Empresa: r.empresa || '', Sucursal: r.sucursal || '',
      Dirección: r.direccion || '', 'Centro de costos': r.centroCostos || '', Justificación: r.justificacion, Partidas: r.partidas, 'Qué se pidió': r.resumenPartidas,
      'Monto estimado': r.montoEstimado, 'Monto adjudicado (OC)': r.montoAdjudicado ?? '', Proveedores: r.proveedores.join(', '), Comprador: r.comprador || '', 'Condición de pago': r.condicionPago || '',
      Resultado: r.resultado === 'rechazada' ? 'Rechazada' : r.resultado === 'completada' ? 'Con solicitud de pago' : 'En proceso',
      'Etapa actual': r.etapaActual, Aprobaciones: aprobacionesTexto(r.aprobaciones).join(' | '),
      Rechazos: r.rechazos.length, 'Último rechazo: etapa': r.ultimoRechazo ? ETAPA_RECHAZO[r.ultimoRechazo.etapa] : '',
      'Último rechazo: motivo': r.ultimoRechazo?.categoria || '', 'Último rechazo: detalle': r.ultimoRechazo?.detalle || '', 'Último rechazo: quién': r.ultimoRechazo?.por || '',
      'Solicitud de pago': r.solicitudes.map(s => `${s.folio}${s.estado ? ` (${ESTADO_PAGO[s.estado] || s.estado})` : ''}`).join(', '),
      'Días a la OC': r.diasHastaOC ?? '', 'Días OC → solicitud': r.diasOCaSolicitud ?? '',
    }));
    const rechazos = data.filas.flatMap(r => r.rechazos.map(x => ({
      Folio: r.folio, Solicitante: r.solicitante || '', Empresa: r.empresa || '', 'Centro de costos': r.centroCostos || '', 'Monto estimado': r.montoEstimado,
      Etapa: ETAPA_RECHAZO[x.etapa] || x.etapa, Motivo: x.categoria, Detalle: x.detalle, 'Rechazó': x.por || '', Fecha: fechaHora(x.fecha),
      'Estado actual': r.etapaActual,
    })));
    const motivos = data.rechazosPorMotivo.map(m => ({ Motivo: m.categoria, Rechazos: m.rechazos, Folios: m.folios.join(', ') }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), 'Requisiciones');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rechazos.length ? rechazos : [{ Folio: 'Sin rechazos en el filtro' }]), 'Rechazos');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(motivos.length ? motivos : [{ Motivo: 'Sin rechazos en el filtro' }]), 'Motivos');
    XLSX.writeFile(wb, `reporte_requisiciones_${new Date().toLocaleDateString('en-CA')}.xlsx`);
    toast_(`Se exportaron ${filas.length} requisiciones.`);
  };

  const r = data?.resumen;
  const o = data?.opciones;
  const filas = data?.filas || [];
  const pages = Math.max(1, Math.ceil(filas.length / POR_PAGINA));
  const visibles = filas.slice((page - 1) * POR_PAGINA, page * POR_PAGINA);
  const maxMotivo = Math.max(1, ...(data?.rechazosPorMotivo || []).map(m => m.rechazos));
  const sel = (k, opciones, todas = 'Todas') => (
    <select value={filtros[k]} onChange={e => setF(k, e.target.value)} style={css.select(false)}>
      <option value="">{todas}</option>
      {opciones.map(x => typeof x === 'string' ? <option key={x} value={x}>{x}</option> : <option key={x.id} value={x.id}>{x.nombre}</option>)}
    </select>
  );
  const td = { padding: '9px 12px', verticalAlign: 'top' };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader title="Reporte de requisiciones"
        subtitle="Todo lo que se pidió por requisición: qué se aprobó y quién lo aprobó, qué se rechazó y por qué, y en qué va cada una."
        actions={<>
          <button type="button" onClick={load} disabled={loading} style={{ ...css.btnGhost, fontSize: 13 }}>Actualizar</button>
          <button type="button" onClick={exportar} disabled={!filas.length} style={{ ...css.btn('secundario'), fontSize: 13 }}>Exportar a Excel</button>
        </>} />

      {/* ── Filtros ── */}
      <div style={{ ...css.card, padding: '14px 18px', marginBottom: 18 }}>
        <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          <div><Label>Desde</Label><input type="date" value={filtros.desde} onChange={e => setF('desde', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Hasta</Label><input type="date" value={filtros.hasta} onChange={e => setF('hasta', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Resultado</Label>
            <select value={filtros.resultado} onChange={e => setF('resultado', e.target.value)} style={css.select(false)}>
              {RESULTADOS.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </div>
          <div><Label>Etapa actual</Label>{sel('etapaActual', o?.etapasActuales || [])}</div>
          <div><Label>Empresa</Label>{sel('empresa', o?.empresas || [])}</div>
          <div><Label>Dirección</Label>{sel('direccion', o?.direcciones || [])}</div>
          <div><Label>Centro de costos</Label>{sel('centroCostos', o?.centros || [], 'Todos')}</div>
          <div><Label>Solicitante</Label>{sel('solicitante', o?.solicitantes || [], 'Todos')}</div>
          <div><Label>Comprador</Label>{sel('comprador', o?.compradores || [], 'Todos')}</div>
          <div><Label>Etapa del rechazo</Label>{sel('etapaRechazo', o?.etapasRechazo || [])}</div>
          <div><Label>Motivo del rechazo</Label>{sel('categoria', o?.categorias || [], 'Todos')}</div>
          <div><Label>Proveedor</Label><input value={texto.proveedor} onChange={e => setTexto(t => ({ ...t, proveedor: e.target.value }))} placeholder="Nombre del proveedor" style={css.input(false)} /></div>
          <div><Label>Buscar</Label><input value={texto.texto} onChange={e => setTexto(t => ({ ...t, texto: e.target.value }))} placeholder="Folio, producto o justificación" style={css.input(false)} /></div>
        </div>
        {hayFiltros && <div style={{ textAlign: 'right', marginTop: 10 }}><button type="button" onClick={limpiar} style={{ ...css.btnGhost, fontSize: 12.5 }}>Quitar filtros</button></div>}
      </div>

      {loading && !data && <Spinner />}
      {error && (
        <div role="alert" style={{ ...css.card, padding: 16, color: 'var(--danger)', display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <span>No se pudo cargar el reporte: {error}</span>
          <button type="button" onClick={load} style={css.btnGhost}>Reintentar</button>
        </div>
      )}

      {r && (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
          {/* ── Resumen: cada tarjeta filtra por su resultado ── */}
          <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 18 }}>
            {[
              { id: '', titulo: 'Solicitadas', n: r.requisiciones, monto: r.montoEstimado, nota: 'monto estimado' },
              { id: 'aprobadas', titulo: 'Aprobadas', n: r.aprobadas, nota: 'pasaron la aprobación de compras' },
              { id: 'rechazada', titulo: 'Rechazadas', n: r.rechazadas, monto: r.montoRechazado, nota: r.tasaRechazoPct != null ? `${r.tasaRechazoPct}% tuvo algún rechazo` : '' },
              { id: 'en_proceso', titulo: 'En proceso', n: r.enProceso, nota: 'todavía sin solicitud de pago' },
              { id: 'completada', titulo: 'Con solicitud de pago', n: r.completadas, nota: 'compra cerrada en el sistema' },
            ].map(c => {
              const activo = filtros.resultado === c.id && (c.id || !filtros.resultado);
              return (
                <button key={c.titulo} type="button" aria-pressed={Boolean(activo)} onClick={() => setF('resultado', c.id)}
                  style={{ ...css.card, textAlign: 'left', padding: '14px 16px', cursor: 'pointer', font: 'inherit', color: 'inherit',
                    borderColor: activo ? 'var(--plasma)' : 'var(--mesh)', background: activo ? 'var(--plasma-dim)' : 'var(--panel)' }}>
                  <div className="nx-label" style={{ marginBottom: 8 }}>{c.titulo}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--digit-font)', fontSize: 26 }}>{c.n}</span>
                    {c.monto != null && <span style={{ fontFamily: 'var(--digit-font)', fontSize: 13, color: 'var(--ink-soft)' }}>{fmtEntero(c.monto)}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 6 }}>{c.nota}</div>
                </button>
              );
            })}
          </div>

          <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginBottom: 18 }}>
            {/* ── Por qué se rechaza ── */}
            <div style={{ ...css.card, padding: '14px 18px' }}>
              <div className="nx-label" style={{ marginBottom: 10 }}>Por qué se rechaza</div>
              {data.rechazosPorMotivo.length ? data.rechazosPorMotivo.map(m => {
                const activo = filtros.categoria === m.categoria;
                return (
                  <button key={m.categoria} type="button" aria-pressed={activo} onClick={() => alternar('categoria', m.categoria)} title="Filtrar por este motivo"
                    style={{ display: 'block', width: '100%', background: activo ? 'var(--plasma-dim)' : 'none', border: 'none', borderRadius: 6, padding: '6px 6px', cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                      <span>{m.categoria}</span>
                      <span style={{ fontFamily: 'var(--digit-font)' }}>{m.rechazos}</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--mesh-soft)', borderRadius: 3, marginTop: 4 }}>
                      <div style={{ width: `${(100 * m.rechazos) / maxMotivo}%`, height: '100%', background: 'var(--danger)', borderRadius: 3 }} />
                    </div>
                  </button>
                );
              }) : <div style={{ fontSize: 13, color: 'var(--glass)' }}>No hubo rechazos en este filtro.</div>}
              {data.rechazosPorEtapa.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--mesh-soft)' }}>
                  {data.rechazosPorEtapa.map(e => (
                    <button key={e.etapa} type="button" aria-pressed={filtros.etapaRechazo === e.etapa} onClick={() => alternar('etapaRechazo', e.etapa)}
                      style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px', borderColor: filtros.etapaRechazo === e.etapa ? 'var(--plasma)' : undefined }}>
                      {e.nombre}: <b>{e.rechazos}</b>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Dónde van y cuánto tardan ── */}
            <div style={{ ...css.card, padding: '14px 18px' }}>
              <div className="nx-label" style={{ marginBottom: 10 }}>En proceso, por etapa</div>
              {r.porEtapa.length ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {r.porEtapa.map(e => (
                      <tr key={e.etapa} style={{ borderTop: '1px solid var(--mesh-soft)', cursor: 'pointer', background: filtros.etapaActual === e.etapa ? 'var(--plasma-dim)' : undefined }}
                        onClick={() => alternar('etapaActual', e.etapa)} title="Filtrar por esta etapa">
                        <td style={{ padding: '6px 4px' }}>{e.etapa}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'var(--digit-font)' }}>{e.requisiciones}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'var(--digit-font)', color: 'var(--ink-soft)' }}>{fmtEntero(e.montoEstimado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div style={{ fontSize: 13, color: 'var(--glass)' }}>Nada en proceso en este filtro.</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--mesh-soft)', fontSize: 12.5 }}>
                <div><div style={{ color: 'var(--glass)' }}>De requisición a orden de compra</div><div style={{ fontFamily: 'var(--digit-font)', fontSize: 18 }}>{dias(r.tiempos.diasHastaOC)}</div></div>
                <div><div style={{ color: 'var(--glass)' }}>De orden de compra a solicitud de pago</div><div style={{ fontFamily: 'var(--digit-font)', fontSize: 18 }}>{dias(r.tiempos.diasOCaSolicitud)}</div></div>
                <div style={{ gridColumn: '1 / -1', color: 'var(--glass)' }}>
                  Medianas. Con orden de compra: {r.compra.requisiciones} · estimado {fmtEntero(r.compra.estimado)} · adjudicado {fmtEntero(r.compra.adjudicado)}
                </div>
              </div>
            </div>
          </div>

          {/* ── Detalle por requisición ── */}
          <div style={{ ...css.card, padding: 0, overflow: 'hidden', marginBottom: 18 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--mesh-soft)', fontFamily: 'var(--label-font)', fontSize: 15, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              {filas.length} {filas.length === 1 ? 'requisición' : 'requisiciones'}
            </div>
            {filas.length ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Folio', 'Solicitante', 'Qué se pidió', 'Estimado', 'Adjudicado', 'Estado', 'Por qué'].map((h, i) => (
                        <th key={h} scope="col" style={{ padding: '9px 12px', textAlign: i === 3 || i === 4 ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map(f => {
                      const ab = abierta === f.id;
                      const aprob = aprobacionesTexto(f.aprobaciones);
                      const ult = f.ultimoRechazo;
                      // Rechazo vigente: rechazada, o Finanzas la rechazó y espera la corrección de Compras
                      const rechazoVigente = ult && (f.resultado === 'rechazada' || f.etapaActual === 'Corrección tras rechazo de Finanzas');
                      return (
                        <Fragment key={f.id}>
                          <tr style={{ borderBottom: ab ? 'none' : '1px solid var(--mesh-soft)', cursor: 'pointer' }} onClick={() => setAbierta(ab ? null : f.id)} aria-expanded={ab}>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                              <div style={{ fontFamily: 'var(--digit-font)', fontSize: 12, color: 'var(--plasma)' }}>{ab ? '▾' : '▸'} {f.folio}</div>
                              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 2 }}>{fecha(f.fecha)}</div>
                            </td>
                            <td style={td}>{f.solicitante || '—'}<div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{f.empresa} · {f.centroCostos}</div></td>
                            <td style={{ ...td, maxWidth: 280 }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} title={f.resumenPartidas}>{f.resumenPartidas || '—'}</div>
                              <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{f.partidas} {f.partidas === 1 ? 'partida' : 'partidas'}{f.proveedores.length ? ` · ${f.proveedores.join(', ')}` : ''}</div>
                            </td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--digit-font)', whiteSpace: 'nowrap' }}>{fmt(f.montoEstimado)}</td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--digit-font)', whiteSpace: 'nowrap' }}>{f.montoAdjudicado != null ? fmt(f.montoAdjudicado) : '—'}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap', color: f.resultado === 'rechazada' ? 'var(--danger)' : f.resultado === 'completada' ? 'var(--ok)' : 'var(--ink)' }}>
                              {f.etapaActual}
                              {f.solicitudes.length > 0 && <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{f.solicitudes.map(s => `${s.folio}${s.estado ? ` · ${ESTADO_PAGO[s.estado] || s.estado}` : ''}`).join(', ')}</div>}
                            </td>
                            <td style={{ ...td, maxWidth: 300, fontSize: 12.5 }}>
                              {rechazoVigente
                                ? <><b style={{ color: 'var(--danger)' }}>{ult.categoria}</b><div style={{ color: 'var(--ink-soft)' }}>{ult.detalle}</div><div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{ETAPA_RECHAZO[ult.etapa]} · {ult.por}</div></>
                                : aprob.length ? <span style={{ color: 'var(--ink-soft)' }}>{aprob[aprob.length - 1]}</span> : <span style={{ color: 'var(--glass)' }}>Sin aprobaciones aún</span>}
                              {!rechazoVigente && f.rechazos.length > 0 && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 2 }}>Tuvo {f.rechazos.length} {f.rechazos.length === 1 ? 'rechazo' : 'rechazos'}</div>}
                            </td>
                          </tr>
                          {ab && (
                            <tr style={{ borderBottom: '1px solid var(--mesh-soft)', background: 'var(--panel-2, var(--plasma-dim))' }}>
                              <td colSpan={7} style={{ padding: '12px 16px 16px' }}>
                                <Detalle f={f} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <div style={{ padding: 20, fontSize: 13, color: 'var(--glass)' }}>No hay requisiciones con estos filtros.</div>}
            <div style={{ padding: '0 16px' }}><Paginacion page={page} pages={pages} onPage={setPage} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// Línea de tiempo de decisiones de una requisición: quién aprobó y quién rechazó
function Detalle({ f }) {
  const a = f.aprobaciones;
  const eventos = [];
  (a.area || []).forEach((p, i) => {
    if (p.estado === 'pendiente') eventos.push({ etapa: `Área${a.area.length > 1 ? ` (paso ${i + 1})` : ''}`, tipo: 'pendiente', por: `Espera a ${p.aprobadores.join(' o ')}` });
    else if (p.estado === 'aprobado') eventos.push({ etapa: `Área${a.area.length > 1 ? ` (paso ${i + 1})` : ''}`, tipo: 'ok', por: p.por, fecha: p.fecha, texto: p.comentario });
  });
  if (a.jefeCompras) eventos.push({ etapa: 'Jefe de compras', tipo: 'ok', por: a.jefeCompras.por, fecha: a.jefeCompras.fecha, texto: a.jefeCompras.comentario });
  if (a.autorizacion) eventos.push({ etapa: 'Autorización de la compra', tipo: 'ok', por: a.autorizacion.por, fecha: a.autorizacion.fecha, texto: a.autorizacion.comentario });
  if (a.finanzas?.estado === 'aprobada') eventos.push({ etapa: 'Finanzas', tipo: 'ok', por: a.finanzas.por, fecha: a.finanzas.fecha, texto: a.finanzas.comentario });
  if (a.finanzas?.estado === 'pendiente') eventos.push({ etapa: 'Finanzas', tipo: 'pendiente', por: 'Esperando validación' });
  f.rechazos.forEach(x => eventos.push({ etapa: ETAPA_RECHAZO[x.etapa] || x.etapa, tipo: 'rechazo', por: x.por, fecha: x.fecha, categoria: x.categoria, texto: x.detalle }));
  eventos.sort((x, y) => (x.fecha ? new Date(x.fecha) : Infinity) - (y.fecha ? new Date(y.fecha) : Infinity));
  const color = { ok: 'var(--ok)', rechazo: 'var(--danger)', pendiente: 'var(--st-pendiente)' };
  const marca = { ok: <Icono nombre="check" size={13} />, rechazo: <Icono nombre="cerrar" size={13} />, pendiente: '…' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: 20, fontSize: 13 }} className="ui-grid">
      <div>
        <div className="nx-label" style={{ marginBottom: 8 }}>Decisiones</div>
        {eventos.length ? eventos.map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderTop: i ? '1px solid var(--mesh-soft)' : 'none' }}>
            <span aria-hidden="true" style={{ color: color[e.tipo], fontWeight: 700, width: 14 }}>{marca[e.tipo]}</span>
            <div style={{ flex: 1 }}>
              <div><b>{e.etapa}</b> · {e.por}{e.fecha && <span style={{ color: 'var(--glass)' }}> · {fechaHora(e.fecha)}</span>}</div>
              {e.categoria && <div style={{ color: 'var(--danger)' }}>{e.categoria}</div>}
              {e.texto && <div style={{ color: 'var(--ink-soft)' }}>{e.texto}</div>}
            </div>
          </div>
        )) : <div style={{ color: 'var(--glass)' }}>Todavía nadie la ha revisado.</div>}
      </div>
      <div>
        <div className="nx-label" style={{ marginBottom: 8 }}>Compra</div>
        <div style={{ lineHeight: 1.7 }}>
          {f.justificacion && <div><span style={{ color: 'var(--glass)' }}>Justificación:</span> {f.justificacion}</div>}
          <div><span style={{ color: 'var(--glass)' }}>Sucursal:</span> {f.sucursal || '—'}{f.direccion ? ` · ${f.direccion}` : ''}</div>
          <div><span style={{ color: 'var(--glass)' }}>Comprador:</span> {f.comprador || '—'}{f.condicionPago ? ` · ${f.condicionPago}` : ''}</div>
          <div><span style={{ color: 'var(--glass)' }}>Proveedores:</span> {f.proveedores.join(', ') || '—'}</div>
          <div><span style={{ color: 'var(--glass)' }}>Días a la orden de compra:</span> {dias(f.diasHastaOC)} · <span style={{ color: 'var(--glass)' }}>a la solicitud:</span> {dias(f.diasOCaSolicitud)}</div>
        </div>
      </div>
    </div>
  );
}
